import { useHistory } from 'react-router-dom';
import { useShowConnectNowCallReports } from '../../../Pages/ConnectNowCallReports/useShowConnectNowCallReport';
import { useShowBeaconNavItem } from '../../../Pages/Beacon/hooks/useShowBeaconNavItem';
import { Flag } from '../../../featureFlags';
import { AppView, Entitlement, Permission, Role } from '../../../graphQL';
import { isProviderSuperAdmin } from '../../../modelUtils/provider';
import { isReportingDisabled } from '../../../utils';
import { useCurrentProvider } from '../../Permissions';
import { useNavbarCounts } from '../Counts';
import { ItemId, allItems, visibleSupportingClinicianItems } from './items';

export const useNavItems = () => {
  const history = useHistory();
  const { currentProvider, appView, featureFlags, hasPermission, showProviderUtilizationTab } =
    useCurrentProvider();
  const hideReporting = isReportingDisabled(currentProvider);

  /*
  const showSuperAdminHome =
    isProviderSuperAdmin(currentProvider) && hasPermission(Permission.Reporting) && !hideReporting;
  */

  /**
   * Only MCP providers can access DDH Crisis Reports page, and only if the entitlement is on
   */
  let providerOrgHasDdh = false;
  if (appView === AppView.Mcp) {
    providerOrgHasDdh = currentProvider.organizations[0].entitlements.some(
      ({ key }) => key === Entitlement.DdhSync
    );
  }

  let providerOrgHasCharlieHealth = false;
  if (appView === AppView.Mcp) {
    const orgEntitlements = currentProvider.organizations[0].entitlements.map(({ key }) => key);
    const requiredEntitlements = [Entitlement.AllowCharlieHealthIopReferralsViaMcp];
    providerOrgHasCharlieHealth = requiredEntitlements.every(ent => orgEntitlements.includes(ent));
  }

  const showStudents = appView === AppView.Mcp && hasPermission(Permission.PatientList);
  const showReferrals =
    (appView === AppView.Mcp || appView === AppView.Referral) &&
    hasPermission(Permission.ReferralsView);

  const permissionMap: Record<ItemId, boolean> = {
    [ItemId.Activity]: hasPermission(Permission.MantraAdmin),
    [ItemId.Announcements]: appView === 'mcp',
    [ItemId.Appointments]: appView === 'oz',
    [ItemId.Beacon]: useShowBeaconNavItem(),
    [ItemId.Config]: hasPermission(Permission.MantraAdmin),
    [ItemId.ConnectNowCallReports]: useShowConnectNowCallReports(),
    [ItemId.CrisisReports]:
      hasPermission(Permission.CrisisReportsLegacyViewOwnorg) && providerOrgHasDdh,
    [ItemId.Dashboard]:
      hasPermission(Permission.CounselorDashboardView) ||
      hasPermission(Permission.ReferrerDashboardView),
    [ItemId.Debug]: featureFlags.includes(Flag.Seed),
    [ItemId.Handbook]: appView === 'mcp',
    [ItemId.Home]: appView === 'oz' && hasPermission(Permission.CareDashboards),
    [ItemId.Iop]: providerOrgHasCharlieHealth || hasPermission(Permission.MantraAdmin),
    [ItemId.Messaging]:
      hasPermission(Permission.PatientMessage) || hasPermission(Permission.ProviderMessage),
    [ItemId.NeedsOutreach]: appView === 'oz' && hasPermission(Permission.MantraAdmin),
    [ItemId.Organizations]: hasPermission(Permission.MantraAdmin),
    [ItemId.Providers]: appView === 'oz' && hasPermission(Permission.MantraAdmin),
    [ItemId.Referrals]: showReferrals,
    [ItemId.Reporting]: hasPermission(Permission.Reporting) && !hideReporting,
    [ItemId.Settings]: true,
    [ItemId.Staff]: appView === 'mcp' && hasPermission(Permission.ProviderList),
    [ItemId.Students]: showStudents,
    [ItemId.Tasks]: appView === 'oz' && hasPermission(Permission.TicketView),
    [ItemId.Users]: appView === 'oz' && hasPermission(Permission.PatientList),
    [ItemId.Utilization]:
      (appView === AppView.Mcp || appView === AppView.Referral) &&
      hasPermission(Permission.UtilizationView) &&
      showProviderUtilizationTab(),
    [ItemId.Verification]: hasPermission(Permission.MantraAdmin),
  };

  const countMap = useNavbarCounts();

  let organizationId: number | undefined;
  if (['referral', 'mcp'].includes(appView)) {
    organizationId = currentProvider.organizations[0].id;
  }

  const filteredItems = allItems.filter(
    i =>
      permissionMap[i.itemId] &&
      (currentProvider.role !== Role.UniversitySupportingClinician ||
        visibleSupportingClinicianItems.has(i.itemId))
  );

  const navItems = filteredItems.map(i => ({
    ...i,
    title:
      i.itemId === ItemId.Dashboard && isProviderSuperAdmin(currentProvider) ? 'Home' : i.title,
    path: i.itemId.replace(':organizationId', String(organizationId)),
    Count: countMap[i.itemId],
  }));

  const activeItem: ItemId | '' = (() => {
    let path = history.location.pathname;
    // make path generic to match with nav_item pathname
    if (path.startsWith('/organizations')) {
      const divided = path.split('/');
      divided[2] = ':organizationId';
      path = `${divided.join('/')}`;
    }
    const directMatch = allItems.find(i => path === i.itemId)?.itemId;
    return (directMatch || allItems.find(i => path.startsWith(i.itemId))?.itemId) ?? '';
  })();

  return { navItems, activeItem };
};
